"use client";

import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useConfirmation } from "@/components/common/ConfirmationDialog";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, FileText, Users } from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import { ActionButton } from "@/components/common/DataViewTypes";
import { useNavigate, useParams } from "react-router-dom";
import { RootState, AppDispatch } from "@/store/store";
import {
  fetchMarketingTemplatesRequest,
  deleteMarketingTemplateRequest,
} from "@/store/slices/marketingTemplate.slice";
import { MarketingTemplate } from "@/types/models/marketing";

const TemplatesPage = () => {
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{
    storeType: string;
    storeId: string;
  }>();
  const dispatch = useDispatch<AppDispatch>();
  const { confirm } = useConfirmation();

  const {
    items: templates,
    loading,
    error: _error,
    pagination,
  } = useSelector((state: RootState) => state.marketingTemplates);

  useEffect(() => {
    dispatch(fetchMarketingTemplatesRequest({}));
  }, [dispatch]);

  const getChannelBadge = (channel: "sms" | "whatsapp" | "email") => {
    const channelColors = {
      email: "bg-blue-500/20 text-blue-600 hover:bg-blue-500/20",
      sms: "bg-green-500/20 text-green-600 hover:bg-green-500/20",
      whatsapp: "bg-emerald-500/20 text-emerald-600 hover:bg-emerald-500/20",
    };

    const channelLabels = {
      email: "Email",
      sms: "SMS",
      whatsapp: "WhatsApp",
    };

    return (
      <Badge className={channelColors[channel]}>{channelLabels[channel]}</Badge>
    );
  };

  const getTypeBadge = (templateType: MarketingTemplate["template_type"]) => {
    const typeColors = {
      campaign: "bg-purple-500/20 text-purple-600 hover:bg-purple-500/20",
      automation: "bg-indigo-500/20 text-indigo-600 hover:bg-indigo-500/20",
      abandoned_cart: "bg-orange-500/20 text-orange-600 hover:bg-orange-500/20",
    };

    const typeLabels = {
      campaign: "Campaign",
      automation: "Automation",
      abandoned_cart: "Abandoned Cart",
    };

    return (
      <Badge className={typeColors[templateType]}>
        {typeLabels[templateType]}
      </Badge>
    );
  };

  const handleDelete = async (templateId: string) => {
    const result = await confirm({
      title: "Delete Template",
      message:
        "Are you sure you want to delete this template? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "destructive",
    });

    if (result.confirmed) {
      dispatch(deleteMarketingTemplateRequest({ id: templateId }));
    }
  };

  const handleEdit = (templateId: string) => {
    navigate(
      `/${storeType}/${storeId}/marketing/templates/${templateId}/edit`,
    );
  };

  const handleRowClick = (template: MarketingTemplate) => {
    handleEdit(template.id);
  };

  const handleCreateNew = () => {
    navigate(`/${storeType}/${storeId}/marketing/templates/create`);
  };

  const columns: ColumnDef<MarketingTemplate>[] = [
    {
      accessorKey: "name",
      header: "Template",
      minSize: 300,
      size: 350,
      cell: ({ row }) => {
        const template = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-foreground">{template.name}</p>
              <p className="text-sm text-muted-foreground">
                {template.description || "No description"}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "channel",
      header: "Channel",
      cell: ({ row }) => {
        return getChannelBadge(row.original.channel);
      },
    },
    {
      accessorKey: "template_type",
      header: "Type",
      cell: ({ row }) => {
        return getTypeBadge(row.original.template_type);
      },
    },
    {
      accessorKey: "usage_count",
      header: "Usage",
      cell: ({ row }) => {
        const template = row.original;
        return (
          <div className="text-sm">
            <div className="flex items-center gap-1 font-medium">
              <Users className="h-3 w-3" />
              {template.usage_count?.toLocaleString() || 0}
            </div>
            <p className="text-muted-foreground">times used</p>
          </div>
        );
      },
    },
    {
      accessorKey: "updated_at",
      header: "Last Updated",
      cell: ({ row }) => {
        return (
          <div className="text-sm text-muted-foreground">
            {new Date(row.original.updated_at).toLocaleDateString()}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const template = row.original;
        return (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleEdit(template.id)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
              onClick={() => handleDelete(template.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  // Create action buttons
  const actionButtons: ActionButton[] = [
    {
      label: "New Template",
      icon: <Plus className="h-3 w-3" />,
      onClick: handleCreateNew,
      variant: "primary",
    },
  ];

  return (
    <div>
      <TableContainer>
        <div className="bg-secondary-background rounded-lg p-4">
          <DataView
            title="Marketing Templates"
            description="Create and manage reusable templates for your marketing campaigns and automations"
            columns={columns}
            data={templates}
            pagination={pagination}
            pageIndex={pagination.page - 1}
            onPageChange={(pageIndex) => {
              dispatch(fetchMarketingTemplatesRequest({ page: pageIndex + 1 }));
            }}
            loading={loading}
            emptyStateType="templates"
            actionButtons={actionButtons}
            enableColumnResizing={true}
            onRowClick={handleRowClick}
          />
        </div>
      </TableContainer>
    </div>
  );
};

export default TemplatesPage;
